import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV2 } from '@retailer-platform/dashboard/routing'
import { campaignManagementRouter } from './routing/campaignManagementRouter'
import { campaignManagementAdminRouter } from './routing/campaignManagementAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  campaignManagementAdminAccessControl,
  campaignManagementAdminCreativesPageAccessControl,
  campaignManagementAdminMezcalPagesControl,
  campaignManagementAdminMultiRetailerPagesViewControl,
  campaignManagementAdminRedemptionUrls,
} from './access-control/campaignManagementAdminAccess.configuration'
import { Permission } from './access-control/permissions'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'campaign-management',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'campaign-management-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [campaignManagementRouter],
    admin: [campaignManagementAdminRouter],
  },
  navigationEntries: [],
  adminNavigationEntries: [
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-theme-page-list',
          labelId: 'campaignManagement.themePages.listPage.title',
          accessControl: campaignManagementAdminAccessControl,
          positionNavV2: 5,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderView,
            },
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-creatives',
          labelId: 'campaignManagement.admin.creativeManager.nav.title',
          accessControl: campaignManagementAdminCreativesPageAccessControl,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-redemption-urls-list',
          labelId: 'campaignManagement.admin.redemptionUrls.title',
          accessControl: campaignManagementAdminRedemptionUrls,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
            {
              slug: Permission.CampaignManagementModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementModify,
            },
          ],
          positionNavV2: 6,
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-multi-retailer-pages-list',
          labelId: 'campaignManagement.admin.multiRetailerPages.title',
          accessControl: campaignManagementAdminMultiRetailerPagesViewControl,
          positionNavV2: 3,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderView,
            },
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Marketing,
      subNavItems: [
        {
          route: 'app-admin-campaign-management-mezcal-pages-list',
          labelId: 'campaignManagement.admin.mezcalPages.title',
          accessControl: campaignManagementAdminMezcalPagesControl,
          positionNavV2: 4,
          inToolPermissions: [
            {
              slug: Permission.CampaignManagementPageBuilderModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.CampaignManagementPageBuilderModify,
            },
          ],
        },
      ],
    },
  ],
}

export default definition
